import type { AuthTokens, User } from "@/lib/api/types";

const KEY = "bsmeet.session";

const REFRESH_MARGIN_MS = 60_000;

export type StoredSession = {
  accessToken: string;
  refreshToken: string;

  expiresAt: number;
  user: User;
};

type Listener = (session: StoredSession | null) => void;

let current: StoredSession | null = load();
const listeners = new Set<Listener>();

function load(): StoredSession | null {
  try {
    const raw = globalThis.localStorage.getItem(KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return typeof parsed.accessToken === "string" ? parsed : null;
  } catch {
    return null;
  }
}

function persist(session: StoredSession | null): void {
  try {
    if (session === null) globalThis.localStorage.removeItem(KEY);
    else globalThis.localStorage.setItem(KEY, JSON.stringify(session));
  } catch (error) {
    console.error("session save failed:", error);
  }
}

export function getSession(): StoredSession | null {
  return current;
}

export function setSession(tokens: AuthTokens, user: User): StoredSession {
  const session: StoredSession = {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: Date.now() + tokens.expiresIn * 1000,
    user,
  };
  current = session;
  persist(session);
  emit();
  return session;
}

export function updateUser(user: User): void {
  if (current === null) return;
  current = { ...current, user };
  persist(current);
  emit();
}

export function clearSession(): void {
  current = null;
  persist(null);
  emit();
}

export function needsRefresh(session: StoredSession, now: number = Date.now()): boolean {
  return session.expiresAt - now < REFRESH_MARGIN_MS;
}

export function startCrossTabSync(): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== KEY) return;
    current = load();
    emit();
  };
  globalThis.addEventListener("storage", onStorage);
  return () => {
    globalThis.removeEventListener("storage", onStorage);
  };
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(): void {
  for (const listener of listeners) listener(current);
}
